import type { APIRemark, APIRemarkFile } from 'types/api.types';
import {
  AUTO_MANDATORY_FILE_VALIDATIONS,
  HOME_MANDATORY_FILE_VALIDATIONS,
} from 'variables/constants';

// Helper to create unique ID for remarks
export const getRemarkId = (remark: APIRemark) =>
  `${remark.validation_key}-${remark.item_key}`;

// Helper to create unique ID for files
export const getFileId = (file: APIRemarkFile) =>
  `${file.validation_key}-${file.item_key}`;

// Helper to get validation name from validation_key
export const getValidationName = (
  validation_key: string,
  validations: Validation[],
) => {
  const validation = validations.find(
    (v) => v.validation_id === validation_key,
  );
  if (validation) {
    return validation.validation_name;
  }
  // Fallback: convert validation_key to readable format
  return validation_key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

// Helper to format item_key (e.g., "vehicle-1" -> "Vehicle 1")
export const formatItemKey = (item_key: string) => {
  if (item_key === 'global') return '';
  return ` (${item_key
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase())})`;
};

// Helper to get remark display label
export const getRemarkLabel = (
  remark: APIRemark,
  validations: Validation[],
) => {
  const validationName = getValidationName(remark.validation_key, validations);
  const itemContext = formatItemKey(remark.item_key);
  return `${validationName}${itemContext}`;
};

// Helper to get file display label
export const getFileLabel = (
  file: APIRemarkFile,
  validations: Validation[],
) => {
  const validationName = getValidationName(file.validation_key, validations);
  const itemContext = formatItemKey(file.item_key);
  return `${validationName}${itemContext}`;
};

// Helper to check if a remark should be disabled (parent validation is selected)
export const isRemarkDisabled = (
  remark: APIRemark,
  selectedValidations: string[],
) => {
  return selectedValidations.includes(remark.validation_key);
};

// Helper to check if a file should be disabled (parent validation is selected)
export const isFileDisabled = (
  file: APIRemarkFile,
  selectedValidations: string[],
) => {
  return selectedValidations.includes(file.validation_key);
};

// Helper to get all child remarks for a validation
export const getChildRemarks = (validationId: string, remarks: APIRemark[]) => {
  return remarks.filter((r) => r.validation_key === validationId);
};

// Helper to get all child files for a validation
export const getChildFiles = (validationId: string, files: APIRemarkFile[]) => {
  return files.filter((f) => f.validation_key === validationId);
};

export const getMandatoryFileValidations = (
  formStateKey: 'autoPersonalForm' | 'homePersonalForm',
): string[] => {
  if (formStateKey === 'autoPersonalForm') {
    return AUTO_MANDATORY_FILE_VALIDATIONS;
  }

  return HOME_MANDATORY_FILE_VALIDATIONS;
};
